/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Typography,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  Select,
  MenuItem,
} from "@mui/material";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { useTheme } from "@mui/material/styles";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import {
  getPortfolioPowerQuery,
  getAlarmDetailQuery,
  getSiteMetadataQuery,
  getBillConfigsQuery,
  calculateBill,
  getCalculationStatus,
  downloadReportCsv,
  downloadFactFinancialsCsv,
  deleteBillCalculationPair,
  assignMonthToPair,
  getAssignedCalculationsQuery,
  getPerformanceReport,
  getPerformanceReportQuery,
  getLatestPerformanceReport,
  getLatestPerformanceReportQuery,
} from "../../lib/Queries";
import AlertBox from "../Shared/AlertBox";
import SitePowerGraph from "../Graphs/Chart/SitePowerGraph";
import SiteDiagram from "../Graphs/Diagram/SiteDiagram";
import Loading from "../Shared/Loading";
import DataComponentError from "../Shared/DataComponentError";
import SiteWeather from "../Dashboard/SiteWeather";
import SiteHeader from "./SiteHeader";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import PropTypes from "prop-types";
import useUserStore from "./../../state/UserStore";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import SiteEquipmentDetail from "./SiteEquipmentDetail";
import SiteSpecs from "./SiteSpecs";
import GraphFavoriteList from "../Graphs/Common/GraphFavoriteList";
import TimeSeriesLineGraphModal from "../Graphs/Common/TimeSeriesLineGraphModal";
import { BillSavingsReportModal } from "../Tariff";
import PerformanceReportTables from "./PerformanceReportTables";
import ReportDetailsModal from "./ReportDetailsModal";
import ViewReportModal from "./ViewReportModal";
import SaveReportModal from "./SaveReportModal";
import RecalculateReportModal from "./RecalculateReportModal";
import ActivityTab from "./ActivityTab";

dayjs.extend(utc);
dayjs.extend(timezone);

const POLLING_INTERVAL_MS = 3000;
const POLLING_TIMEOUT_MS = 180000; // 3 minutes
/** Banner + Performance tab indicator only while latest scheduler report is within this age. */
const RECENT_AUTO_REPORT_WINDOW_MS = 48 * 60 * 60 * 1000;

/**
 * Billing APIs serialize UTC timestamps without a Z suffix (naive UTC). `new Date(iso)` would
 * treat those as local wall time and make `calculated_at` appear in the future for US timezones,
 * hiding the auto-report banner and tab dot.
 */
const ISO_HAS_EXPLICIT_OFFSET = /[zZ]$|[+-]\d{2}:?\d{2}$/;

/**
 * Report CSVs are named "<site name> - <download option> - <Mon YYYY>.csv".
 * An unknown month becomes "Latest" instead of "Invalid Date", since the
 * download endpoints fall back to the site's latest billing period.
 */
const reportCsvName = (siteName, option, month) => {
  const parsed = dayjs(month || null);
  const period = parsed.isValid() ? parsed.format("MMM YYYY") : "Latest";
  return `${siteName} - ${option} - ${period}.csv`;
};

function parseBillingTimestampMs(iso) {
  if (iso == null || iso === "") return NaN;
  const s = String(iso).trim();
  if (!s) return NaN;
  if (ISO_HAS_EXPLICIT_OFFSET.test(s)) {
    return dayjs(s).valueOf();
  }
  return dayjs.utc(s).valueOf();
}

const Site = ({ flags }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [alertData, setAlertData] = useState(null);
  const [activeTab, setActiveTab] = useState("summary");
  const { sitename } = useParams();
  const [searchParams] = useSearchParams();
  const [displaySite, setDisplaySite] = useState(null);
  const alarmUuid = searchParams.get("alarm");
  const [graphFavoritesIsOpen, setGraphFavoritesIsOpen] = useState(false);
  const [selectedGraphFavorite, setSelectedGraphFavorite] = useState(null);

  // Bill Savings Report modal state
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);

  // Report Details modal state
  const [reportDetailsOpen, setReportDetailsOpen] = useState(false);

  // Ephemeral report modal state (ViewReportModal)
  const [viewReportModalOpen, setViewReportModalOpen] = useState(false);
  const [ephemeralReport, setEphemeralReport] = useState(null);
  const [ephemeralPairId, setEphemeralPairId] = useState(null);
  const [isDiscarding, setIsDiscarding] = useState(false);

  // Derived: modal is loading when it's open but report data hasn't arrived yet
  const isLoadingEphemeral = viewReportModalOpen && !ephemeralReport && !isDiscarding;

  // Save Report modal state
  const [saveReportModalOpen, setSaveReportModalOpen] = useState(false);
  const [isSavingReport, setIsSavingReport] = useState(false);

  // Recalculate Report modal state
  const [recalculateModalOpen, setRecalculateModalOpen] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);

  // Fact Financials export state
  const [isExportingFactFinancials, setIsExportingFactFinancials] = useState(false);

  // Download dropdown state
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);

  // Saved reports: selected month for the dropdown
  const [selectedSavedMonth, setSelectedSavedMonth] = useState("");

  // Calculation polling state
  const [calculatingIds, setCalculatingIds] = useState(null); // { pre: number, post: number, pairId: string }
  const pollingTimerRef = useRef(null);
  const pollingStartRef = useRef(null);

  const theme = useTheme();
  const userStore = useUserStore();

  const InvalidSite = () => (
    <div className="chart-graph">
      <Typography
        width="100%"
        height="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        variant="h5"
      >
        This site is not yet operational
      </Typography>
    </div>
  );

  const {
    isLoading: isMetadataLoading,
    error: metadataError,
    data: siteMetadata,
  } = useQuery({
    ...getSiteMetadataQuery(displaySite?.uuid),
    enabled: Boolean(displaySite?.uuid),
  });

  // Synchronous so Pre-Commissioning and Retired never mounts Overview children for one paint.
  const siteValid = useMemo(() => {
    if (!siteMetadata?.state) return false;
    return (
      siteMetadata.state === "commissioned" ||
      siteMetadata.state === "Operating" ||
      siteMetadata.state === "Commissioning"
    );
  }, [siteMetadata?.state]);

  const { data: siteData } = useQuery(getPortfolioPowerQuery());

  const { error: alarmDetailError, data: alarmDetailData } = useQuery({
    ...getAlarmDetailQuery(displaySite?.uuid, alarmUuid),
    enabled: Boolean(displaySite?.uuid && alarmUuid && siteValid),
  });

  // Fetch bill configurations for the site
  const { data: billConfigs = [] } = useQuery({
    ...getBillConfigsQuery(siteMetadata?.uuid),
    enabled: !!siteMetadata?.uuid && siteValid,
  });

  // Fetch all assigned (saved) calculation pairs for this site
  const { isLoading: isAssignedLoading, data: assignedCalculations = [] } = useQuery({
    ...getAssignedCalculationsQuery(siteMetadata?.uuid),
    enabled: !!siteMetadata?.uuid && siteValid,
  });

  // API may return multiple pairs for the same calendar month; keep one row per month
  // (latest assignment wins) so the Saved Reports dropdown does not list duplicates.
  const assignedCalculationsDeduped = useMemo(() => {
    const pickPreferred = (a, b) => {
      const ta = a.assigned_at ? new Date(a.assigned_at).getTime() : 0;
      const tb = b.assigned_at ? new Date(b.assigned_at).getTime() : 0;
      if (tb !== ta) return tb > ta ? b : a;
      return a.pair_id > b.pair_id ? a : b;
    };
    const byMonth = new Map();
    for (const calc of assignedCalculations) {
      const month = calc.assigned_month;
      const existing = byMonth.get(month);
      byMonth.set(month, existing ? pickPreferred(existing, calc) : calc);
    }
    return Array.from(byMonth.values());
  }, [assignedCalculations]);

  // Fetch the latest performance report at site page load to detect
  // recent scheduler-generated reports (for the banner + tab dot)
  const { data: latestReport } = useQuery({
    ...getLatestPerformanceReportQuery(siteMetadata?.uuid),
    enabled: !!siteMetadata?.uuid && siteValid,
  });

  /** Bumps when the 48h window may have elapsed so banner/tab dot update without a full refresh. */
  const [autoReportRecheckTick, setAutoReportRecheckTick] = useState(0);

  useEffect(() => {
    if (latestReport?.triggered_by !== "scheduler" || !latestReport?.calculated_at) {
      return undefined;
    }
    const calculatedAt = parseBillingTimestampMs(latestReport.calculated_at);
    if (Number.isNaN(calculatedAt)) {
      return undefined;
    }
    const expiresAt = calculatedAt + RECENT_AUTO_REPORT_WINDOW_MS;
    const delay = expiresAt - Date.now();
    if (delay <= 0) {
      return undefined;
    }
    const id = window.setTimeout(() => setAutoReportRecheckTick((n) => n + 1), delay);
    return () => window.clearTimeout(id);
  }, [latestReport?.calculated_at, latestReport?.triggered_by]);

  const hasRecentAutoReport = useMemo(() => {
    if (latestReport?.triggered_by !== "scheduler" || !latestReport?.calculated_at) {
      return false;
    }
    const calculatedAt = parseBillingTimestampMs(latestReport.calculated_at);
    if (Number.isNaN(calculatedAt)) {
      return false;
    }
    const ageMs = Date.now() - calculatedAt;
    return ageMs >= 0 && ageMs < RECENT_AUTO_REPORT_WINDOW_MS;
  }, [latestReport, autoReportRecheckTick]);

  const [autoReportBannerDismissed, setAutoReportBannerDismissed] = useState(false);

  // Auto-select the most recent saved month when data loads
  useEffect(() => {
    if (assignedCalculationsDeduped.length > 0 && !selectedSavedMonth) {
      const sorted = [...assignedCalculationsDeduped].sort(
        (a, b) => new Date(b.assigned_month) - new Date(a.assigned_month),
      );
      setSelectedSavedMonth(sorted[0].assigned_month);
    }
  }, [assignedCalculationsDeduped, selectedSavedMonth]);

  // When leaving the Performance tab, reset to the latest saved report so returning
  // to Performance does not keep showing a previously selected older report.
  const prevActiveTabRef = useRef(activeTab);
  useEffect(() => {
    if (prevActiveTabRef.current === "performance" && activeTab !== "performance") {
      if (assignedCalculationsDeduped.length > 0) {
        const sorted = [...assignedCalculationsDeduped].sort(
          (a, b) => new Date(b.assigned_month) - new Date(a.assigned_month),
        );
        setSelectedSavedMonth(sorted[0].assigned_month);
      }
    }
    prevActiveTabRef.current = activeTab;
  }, [activeTab, assignedCalculationsDeduped]);

  // Find the selected saved calculation
  const selectedSavedCalc = assignedCalculationsDeduped.find(
    (c) => c.assigned_month === selectedSavedMonth,
  );

  // Fetch the performance report for the selected saved period
  const {
    isLoading: isReportLoading,
    error: reportError,
    data: reportData,
  } = useQuery({
    ...getPerformanceReportQuery(
      siteMetadata?.uuid,
      selectedSavedCalc?.billing_cycle_start,
      selectedSavedCalc?.billing_cycle_end,
    ),
    enabled: !!siteMetadata?.uuid && !!selectedSavedCalc && siteValid,
  });

  // Find pre and post microgrid configs from the fetched list
  const preConfig = Array.isArray(billConfigs)
    ? billConfigs.find((c) => c.bill_type === "pre_microgrid" && c.active)
    : null;
  const postConfig = Array.isArray(billConfigs)
    ? billConfigs.find((c) => c.bill_type === "post_microgrid" && c.active)
    : null;

  useEffect(() => {
    const normalizedName = sitename?.replaceAll("-", " ");
    const matchedSite = siteData?.sites?.find(
      (site) => site.name.replaceAll("-", " ") === normalizedName,
    );
    if (matchedSite) {
      localStorage.setItem("site", matchedSite.uuid);
      setDisplaySite(matchedSite);
    }
  }, [siteData?.sites, sitename]);

  // Polling logic for calculation status
  const stopPolling = useCallback(() => {
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
    pollingStartRef.current = null;
    setCalculatingIds(null);
  }, []);

  const pollCalculationStatus = useCallback(async () => {
    if (!calculatingIds) return;

    // Check for timeout
    if (pollingStartRef.current && Date.now() - pollingStartRef.current > POLLING_TIMEOUT_MS) {
      stopPolling();
      setViewReportModalOpen(false);
      setEphemeralReport(null);
      setEphemeralPairId(null);
      setRecalculateModalOpen(false);
      setIsRecalculating(false);
      enqueueSnackbar(
        "Calculation is taking longer than expected. Please refresh to check later.",
        {
          variant: "warning",
          autoHideDuration: 6000,
        },
      );
      return;
    }

    try {
      const statuses = await Promise.all([
        calculatingIds.pre ? getCalculationStatus(calculatingIds.pre) : null,
        calculatingIds.post ? getCalculationStatus(calculatingIds.post) : null,
      ]);

      const preStatus = statuses[0]?.status;
      const postStatus = statuses[1]?.status;

      // Check for failures
      if (preStatus === "failed" || postStatus === "failed") {
        const failedMsg =
          statuses[0]?.error_message || statuses[1]?.error_message || "Unknown error";
        stopPolling();
        setViewReportModalOpen(false);
        setEphemeralReport(null);
        setEphemeralPairId(null);
        setRecalculateModalOpen(false);
        setIsRecalculating(false);
        enqueueSnackbar(`Bill calculation failed: ${failedMsg}`, {
          variant: "error",
          autoHideDuration: 30000,
        });
        return;
      }

      // Check if both are completed
      const preCompleted = !calculatingIds.pre || preStatus === "completed";
      const postCompleted = !calculatingIds.post || postStatus === "completed";

      if (preCompleted && postCompleted) {
        const cycleStart = statuses[0]?.billing_cycle_start || statuses[1]?.billing_cycle_start;
        const cycleEnd = statuses[0]?.billing_cycle_end || statuses[1]?.billing_cycle_end;
        stopPolling();

        if (viewReportModalOpen && siteMetadata?.uuid) {
          // Ephemeral flow: populate the already-open modal with report data
          try {
            let report;
            if (cycleStart && cycleEnd) {
              report = await getPerformanceReport(siteMetadata.uuid, cycleStart, cycleEnd);
            } else {
              report = await getLatestPerformanceReport(siteMetadata.uuid);
            }
            setEphemeralReport(report);
          } catch (fetchError) {
            console.error("Error fetching performance report after calculation:", fetchError);
            try {
              const fallbackReport = await getLatestPerformanceReport(siteMetadata.uuid);
              setEphemeralReport(fallbackReport);
            } catch (fallbackError) {
              console.error("Fallback report fetch also failed:", fallbackError);
              enqueueSnackbar("Report generated but could not be loaded. Please refresh.", {
                variant: "warning",
                autoHideDuration: 6000,
              });
              setViewReportModalOpen(false);
              setEphemeralPairId(null);
            }
          }
        } else {
          // Recalculate flow: refresh saved report queries so the page updates
          queryClient.invalidateQueries({ queryKey: ["assignedCalculations"] });
          queryClient.invalidateQueries({ queryKey: ["performanceReport"] });
          setRecalculateModalOpen(false);
          setIsRecalculating(false);
        }

        enqueueSnackbar("Report generated successfully!", {
          variant: "success",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Error polling calculation status:", error);
      // Don't stop polling on transient errors
    }
  }, [
    calculatingIds,
    stopPolling,
    enqueueSnackbar,
    queryClient,
    siteMetadata,
    viewReportModalOpen,
  ]);

  useEffect(() => {
    if (calculatingIds) {
      pollingStartRef.current = Date.now();
      pollingTimerRef.current = setInterval(pollCalculationStatus, POLLING_INTERVAL_MS);
      // Run immediately on first call
      pollCalculationStatus();
    }
    return () => {
      if (pollingTimerRef.current) {
        clearInterval(pollingTimerRef.current);
      }
    };
  }, [calculatingIds, pollCalculationStatus]);

  const getAlarmDetailErrorMessage = () => {
    let message = "There was an error retrieving the requested alarm";
    if (alarmDetailError?.response?.status === 404) {
      message = "The requested alarm was not found";
    }
    if (alarmDetailData) {
      if (alarmDetailData.alarms.length > 0) {
        let alarm = alarmDetailData.alarms[0];
        if (alarm.stop_time) {
          message = `The alarm, ${alarm.name} for ${alarm.type} occurring at ${alarm.date} is no longer active!`;
        }
      }
    }
    return message;
  };

  const showAlarmDetailErrorMessage = () => {
    if (alarmDetailError) {
      return true;
    }
    if (alarmDetailData?.alarms?.length > 0) {
      if (alarmDetailData.alarms[0].stop_time) {
        return true;
      }
    }
    return false;
  };

  const handleShowGraphFavorites = (graphFavorite) => {
    setSelectedGraphFavorite(graphFavorite);
    setGraphFavoritesIsOpen(true);
  };

  const handleCloseGraphFavorites = () => {
    setGraphFavoritesIsOpen(false);
    setSelectedGraphFavorite(null);
  };

  const isCalculating = !!calculatingIds;

  const handleNewReportClick = () => {
    setReportModalOpen(true);
  };

  const handleReportSubmit = async (data) => {
    setIsSubmittingReport(true);
    try {
      const { startDate, endDate, preConfig: submitPreConfig, postConfig: submitPostConfig } = data;

      if (!submitPreConfig) {
        enqueueSnackbar(
          "No tariff configuration found. Please configure tariffs in Site Settings first.",
          {
            variant: "error",
            autoHideDuration: 30000,
          },
        );
        return;
      }

      // Convert calendar-selected dates to the site's local timezone.
      // The DatePicker returns a dayjs object at midnight in the browser's
      // timezone. We extract the bare date string ("YYYY-MM-DD") and
      // re-interpret it as midnight in the site's timezone so the billing
      // boundaries align with the site's local clock, not the user's browser.
      const siteTimezone = siteMetadata.timezone;
      const startDateISO = dayjs.tz(startDate.format("YYYY-MM-DD"), siteTimezone).toISOString();
      const endDateISO = dayjs.tz(endDate.format("YYYY-MM-DD"), siteTimezone).toISOString();

      const request = {
        site_uuid: siteMetadata.uuid,
        pre_config_id: submitPreConfig.config_id,
        post_config_id: submitPostConfig ? submitPostConfig.config_id : undefined,
        billing_cycle_start: startDateISO,
        billing_cycle_end: endDateISO,
        time_series_interval: 900,
      };

      const result = await calculateBill(request);

      const hasDistinctConfigs =
        submitPostConfig && submitPostConfig.config_id !== submitPreConfig.config_id;
      const message = hasDistinctConfigs
        ? "Pre-project and post-project bill calculations queued successfully."
        : "Bill calculation queued successfully (same configuration for pre and post).";

      enqueueSnackbar(message, {
        variant: "success",
        autoHideDuration: 3000,
      });

      setReportModalOpen(false);

      // Open the ViewReportModal immediately in loading state
      const pairId = result.pre_calculation?.pair_id || result.post_calculation?.pair_id || null;
      setEphemeralPairId(pairId);
      setEphemeralReport(null);
      setViewReportModalOpen(true);

      // Enter Calculating state and start polling
      setCalculatingIds({
        pre: result.pre_calculation?.calculation_id || null,
        post: result.post_calculation?.calculation_id || null,
        pairId,
      });
    } catch (error) {
      console.error("Error generating report:", error);
      enqueueSnackbar("Failed to queue bill calculation. Please try again.", {
        variant: "error",
        autoHideDuration: 30000,
      });
    } finally {
      setIsSubmittingReport(false);
    }
  };

  const handleDownloadAllData = async () => {
    if (!siteMetadata?.uuid) return;
    try {
      await downloadReportCsv(
        siteMetadata.uuid,
        reportCsvName(siteMetadata.site_name, "Tariff Breakdown", selectedSavedMonth),
        selectedSavedCalc?.billing_cycle_start,
        selectedSavedCalc?.billing_cycle_end,
      );
    } catch (error) {
      console.error("Error downloading report data:", error);
      enqueueSnackbar("Failed to download report data.", {
        variant: "error",
        autoHideDuration: 30000,
      });
    }
  };

  // Export the Utility Cost Summary (Fact Financials schema) for the selected saved month
  const handleExportFactFinancials = async () => {
    if (!siteMetadata?.uuid || !selectedSavedCalc) return;
    setIsExportingFactFinancials(true);
    try {
      await downloadFactFinancialsCsv(
        siteMetadata.uuid,
        selectedSavedCalc.billing_cycle_start,
        selectedSavedCalc.billing_cycle_end,
        reportCsvName(siteMetadata.site_name, "Utility Cost Summary", selectedSavedMonth),
      );
    } catch (error) {
      console.error("Error exporting fact financials:", error);
      enqueueSnackbar("Failed to export Utility Cost Summary.", {
        variant: "error",
        autoHideDuration: 30000,
      });
    } finally {
      setIsExportingFactFinancials(false);
    }
  };

  // Discard the ephemeral report (hard delete the pair)
  const handleDiscardEphemeralReport = async () => {
    if (!ephemeralPairId) {
      setViewReportModalOpen(false);
      setEphemeralReport(null);
      setEphemeralPairId(null);
      return;
    }

    setIsDiscarding(true);
    try {
      await deleteBillCalculationPair(ephemeralPairId);
    } catch (error) {
      console.error("Error discarding report:", error);
      enqueueSnackbar("Failed to discard report.", {
        variant: "error",
        autoHideDuration: 6000,
      });
    } finally {
      setIsDiscarding(false);
      setViewReportModalOpen(false);
      setEphemeralReport(null);
      setEphemeralPairId(null);
    }
  };

  // Open the Save Report modal from within the View Report modal
  const handleOpenSaveModal = () => {
    setSaveReportModalOpen(true);
  };

  // Save the ephemeral report by assigning it to a month
  const handleSaveReportToMonth = async (assignedMonth) => {
    if (!ephemeralPairId) return;

    setIsSavingReport(true);
    try {
      await assignMonthToPair(ephemeralPairId, { assigned_month: assignedMonth });

      enqueueSnackbar("Report saved successfully!", {
        variant: "success",
        autoHideDuration: 3000,
      });

      // Close both modals
      setSaveReportModalOpen(false);
      setViewReportModalOpen(false);
      setEphemeralReport(null);
      setEphemeralPairId(null);

      // Refresh assigned calculations and switch to saved month
      queryClient.invalidateQueries({ queryKey: ["assignedCalculations"] });
      setSelectedSavedMonth(assignedMonth);
    } catch (error) {
      console.error("Error saving report:", error);
      enqueueSnackbar("Failed to save report. Please try again.", {
        variant: "error",
        autoHideDuration: 6000,
      });
    } finally {
      setIsSavingReport(false);
    }
  };

  // Handle View Details from within the ephemeral modal
  const handleEphemeralViewDetails = () => {
    setReportDetailsOpen(true);
  };

  // Handle Download Data from within the ephemeral modal
  const handleEphemeralDownloadData = async () => {
    if (!siteMetadata?.uuid) return;
    try {
      await downloadReportCsv(
        siteMetadata.uuid,
        reportCsvName(
          siteMetadata.site_name,
          "Tariff Breakdown",
          ephemeralReport?.billing_cycle_start,
        ),
        ephemeralReport?.billing_cycle_start,
        ephemeralReport?.billing_cycle_end,
      );
    } catch (error) {
      console.error("Error downloading report data:", error);
      enqueueSnackbar("Failed to download report data.", {
        variant: "error",
        autoHideDuration: 30000,
      });
    }
  };

  // Handle Recalculate: re-run the bill calculation with current tariffs
  // but using the billing period dates and assigned month from the saved report.
  // The new pair is immediately assigned to the same month, overwriting the old one.
  const handleRecalculate = async () => {
    if (!siteMetadata?.uuid || !selectedSavedCalc || !preConfig) return;

    setIsRecalculating(true);
    try {
      const siteTimezone = siteMetadata.timezone;
      const startISO = dayjs.tz(selectedSavedCalc.billing_cycle_start, siteTimezone).toISOString();
      const endISO = dayjs.tz(selectedSavedCalc.billing_cycle_end, siteTimezone).toISOString();

      const request = {
        site_uuid: siteMetadata.uuid,
        pre_config_id: preConfig.config_id,
        post_config_id: postConfig ? postConfig.config_id : undefined,
        billing_cycle_start: startISO,
        billing_cycle_end: endISO,
        time_series_interval: 900,
      };

      const result = await calculateBill(request);

      const pairId = result.pre_calculation?.pair_id || result.post_calculation?.pair_id || null;

      // Immediately assign the new pair to the same month so it overwrites the old report
      if (pairId && selectedSavedMonth) {
        await assignMonthToPair(pairId, {
          assigned_month: selectedSavedMonth,
        });
      }

      // Keep the modal open with the button spinning while polling runs.
      // The polling callbacks will close the modal and reset isRecalculating.
      setCalculatingIds({
        pre: result.pre_calculation?.calculation_id || null,
        post: result.post_calculation?.calculation_id || null,
        pairId,
      });
    } catch (error) {
      console.error("Error recalculating report:", error);
      enqueueSnackbar("Report calculation failed. Please try again.", {
        variant: "error",
        autoHideDuration: 30000,
      });
      setRecalculateModalOpen(false);
      setIsRecalculating(false);
    }
  };

  /**
   * Render the Performance tab content based on current state.
   */
  const renderPerformanceContent = () => {
    // State: Loading (initial load of assigned calculations)
    if (isAssignedLoading) {
      return (
        <Box>
          <Loading />
        </Box>
      );
    }

    // State: No saved reports exist
    const hasSavedReports = assignedCalculationsDeduped.length > 0;
    if (!hasSavedReports) {
      return (
        <Box>
          {userStore.isAdmin() && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Button variant="contained" color="primary" onClick={handleNewReportClick}>
                New Report
              </Button>
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "300px",
              color: "text.secondary",
            }}
          >
            <AssessmentOutlinedIcon fontSize="large" sx={{ mb: 2, opacity: 0.4 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              No Performance Reports
            </Typography>
            <Typography variant="body2">
              Create a report to compare baseline and actual utility costs.
            </Typography>
          </Box>
        </Box>
      );
    }

    // State: Loading selected saved report
    if (isReportLoading) {
      return (
        <Box>
          <Loading />
        </Box>
      );
    }

    // State: Error loading report data
    if (reportError) {
      return (
        <Box>
          <DataComponentError />
        </Box>
      );
    }

    // State: Saved report displayed
    const savedMonthTitle = selectedSavedMonth ? dayjs(selectedSavedMonth).format("MMMM YYYY") : "";
    const startDate = reportData?.billing_cycle_start
      ? dayjs(reportData.billing_cycle_start).format("MMM D")
      : "";
    const endDate = reportData?.billing_cycle_end
      ? dayjs(reportData.billing_cycle_end).format("MMM D YYYY")
      : "";

    // Build dropdown options sorted descending
    const sortedAssigned = [...assignedCalculationsDeduped].sort(
      (a, b) => new Date(b.assigned_month) - new Date(a.assigned_month),
    );

    return (
      <Box>
        {/* Title row: Month title + Saved Reports dropdown */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 600, fontSize: "34px", lineHeight: 1.235, letterSpacing: "0.25px" }}
            >
              {savedMonthTitle}
            </Typography>
            {startDate && endDate && (
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  fontSize: "16px",
                  lineHeight: 1.75,
                  letterSpacing: "0.15px",
                  color: "text.secondary",
                }}
              >
                {startDate} - {endDate}
              </Typography>
            )}
          </Box>

          <FormControl variant="outlined" size="small" sx={{ minWidth: 214 }}>
            <InputLabel id="saved-reports-label">Saved Reports</InputLabel>
            <Select
              labelId="saved-reports-label"
              id="saved-reports-select"
              value={selectedSavedMonth}
              label="Saved Reports"
              onChange={(e) => setSelectedSavedMonth(e.target.value)}
            >
              {sortedAssigned.map((calc) => (
                <MenuItem key={calc.pair_id} value={calc.assigned_month}>
                  {dayjs(calc.assigned_month).format("MMMM YYYY")}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => setReportDetailsOpen(true)}
          >
            View Details
          </Button>
          <IconButton
            size="small"
            color="secondary"
            onClick={() => setRecalculateModalOpen(true)}
            title="Recalculate Report"
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: "4px",
            }}
          >
            <RefreshOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton
            id="download-report-button"
            size="small"
            color="secondary"
            onClick={(event) => setDownloadMenuAnchor(event.currentTarget)}
            title="Download"
            aria-label="Download"
            aria-haspopup="menu"
            aria-controls={downloadMenuAnchor ? "download-report-menu" : undefined}
            aria-expanded={Boolean(downloadMenuAnchor)}
            disabled={isExportingFactFinancials}
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: "4px",
            }}
          >
            {isExportingFactFinancials ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <DownloadOutlinedIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
          <Menu
            id="download-report-menu"
            anchorEl={downloadMenuAnchor}
            open={Boolean(downloadMenuAnchor)}
            onClose={() => setDownloadMenuAnchor(null)}
            MenuListProps={{ "aria-labelledby": "download-report-button" }}
          >
            <MenuItem
              disabled={!selectedSavedCalc}
              onClick={() => {
                setDownloadMenuAnchor(null);
                handleExportFactFinancials();
              }}
            >
              Utility Cost Summary (CSV)
            </MenuItem>
            <MenuItem
              onClick={() => {
                setDownloadMenuAnchor(null);
                handleDownloadAllData();
              }}
            >
              Tariff Breakdown (CSV)
            </MenuItem>
          </Menu>
          <Box sx={{ flex: 1 }} />
          {userStore.isAdmin() && (
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={handleNewReportClick}
            >
              New Report
            </Button>
          )}
        </Box>

        {/* Auto-generated report banner */}
        {hasRecentAutoReport && !autoReportBannerDismissed && (
          <Alert
            severity="info"
            icon={<ReceiptLongOutlinedIcon />}
            onClose={() => setAutoReportBannerDismissed(true)}
            sx={{ mb: 2 }}
          >
            This report was automatically generated recently.
          </Alert>
        )}

        {/* Report Tables */}
        {reportData && <PerformanceReportTables report={reportData} />}
      </Box>
    );
  };

  const getContent = () => {
    let content;
    if (metadataError) {
      content = (
        <div className="chart-graph">
          <DataComponentError />
        </div>
      );
    } else if (isMetadataLoading) {
      content = (
        <div className="chart-graph">
          <Loading />
        </div>
      );
    } else {
      content = (
        <>
          {activeTab === "summary" ? (
            <>
              {siteValid ? (
                <>
                  <SiteDiagram site={siteMetadata} />
                  <div className="spacer"></div>
                  <SitePowerGraph site={siteMetadata} />
                  <GraphFavoriteList
                    site={siteMetadata}
                    handleShowGraphFavorites={handleShowGraphFavorites}
                    graphFavoritesIsOpen={graphFavoritesIsOpen}
                  />
                  <TimeSeriesLineGraphModal
                    isOpen={graphFavoritesIsOpen}
                    setIsOpen={setGraphFavoritesIsOpen}
                    onClose={handleCloseGraphFavorites}
                    graphFavorite={selectedGraphFavorite}
                    site={siteMetadata}
                  />
                </>
              ) : (
                <InvalidSite />
              )}
            </>
          ) : null}

          {activeTab === "detail" ? (
            <>{siteValid ? <SiteEquipmentDetail site={siteMetadata} /> : <InvalidSite />}</>
          ) : null}
          {activeTab === "activity" ? (
            <>{siteValid ? <ActivityTab site={siteMetadata} /> : <InvalidSite />}</>
          ) : null}
          {activeTab === "specs" ? (
            <>{siteValid ? <SiteSpecs site={siteMetadata} /> : <InvalidSite />}</>
          ) : null}
          {activeTab === "performance" ? (
            <>
              {siteValid ? (
                <Box display="flex" flexDirection="column" sx={theme.classes.genericPanel}>
                  <Box mb={7}>
                    <Typography variant="subtitle2" color="secondary">
                      Performance
                    </Typography>
                  </Box>
                  <Box px={7}>{renderPerformanceContent()}</Box>
                </Box>
              ) : (
                <InvalidSite />
              )}
            </>
          ) : null}
        </>
      );
    }
    return content;
  };

  return (
    <div className="site-scroll">
      <div className="site-container">
        <div className="table-div">
          {showAlarmDetailErrorMessage() ? (
            <Box pb="2rem">
              <AlertBox
                type={"success"}
                message={getAlarmDetailErrorMessage()}
                openAlert={true}
                clearAlert={() => setAlertData(null)}
              />
            </Box>
          ) : null}
          <SiteHeader
            metadata={siteMetadata}
            setActive={setActiveTab}
            hasRecentAutoReport={hasRecentAutoReport}
            sites={siteData?.sites}
            siteValid={siteValid}
          />
          {flags.siteWeather ? (
            <Box mb="10px">
              <SiteWeather />
            </Box>
          ) : null}
          <div className="spacer"></div>

          {getContent()}
        </div>
      </div>

      {/* Bill Savings Report Modal (create new report) */}
      <BillSavingsReportModal
        open={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        onSubmit={handleReportSubmit}
        preConfig={preConfig}
        postConfig={postConfig}
        isSubmitting={isSubmittingReport}
      />

      {/* View Report Modal (ephemeral report after calculation) */}
      <ViewReportModal
        open={viewReportModalOpen}
        report={ephemeralReport}
        isLoading={isLoadingEphemeral}
        onDiscard={handleDiscardEphemeralReport}
        onSaveReport={handleOpenSaveModal}
        onViewDetails={handleEphemeralViewDetails}
        onDownloadData={handleEphemeralDownloadData}
        isDiscarding={isDiscarding}
      />

      {/* Save Report Modal (assign ephemeral report to a month) */}
      <SaveReportModal
        open={saveReportModalOpen}
        onClose={() => setSaveReportModalOpen(false)}
        onSave={handleSaveReportToMonth}
        billingCycleStart={ephemeralReport?.billing_cycle_start}
        billingCycleEnd={ephemeralReport?.billing_cycle_end}
        isSaving={isSavingReport}
      />

      {/* Recalculate Report Modal */}
      <RecalculateReportModal
        open={recalculateModalOpen}
        onClose={() => setRecalculateModalOpen(false)}
        onRecalculate={handleRecalculate}
        preConfig={preConfig}
        postConfig={postConfig}
        billingCycleStart={selectedSavedCalc?.billing_cycle_start}
        billingCycleEnd={selectedSavedCalc?.billing_cycle_end}
        assignedMonth={selectedSavedMonth}
        isRecalculating={isRecalculating}
      />

      {/* Report Details Modal */}
      <ReportDetailsModal
        open={reportDetailsOpen}
        onClose={() => setReportDetailsOpen(false)}
        report={viewReportModalOpen ? ephemeralReport : reportData}
        billConfigs={billConfigs}
        siteUuid={siteMetadata?.uuid}
        siteTimezone={siteMetadata?.timezone}
      />
    </div>
  );
};

Site.propTypes = {
  flags: PropTypes.object,
};

export default withLDConsumer()(Site);
